import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { css } from '@emotion/react';
import { ArrowSquareOut, BookOpenText, Copy, HardDrives, Plus, PlusCircle } from '@phosphor-icons/react';
import { getPlaceholderUrl } from 'common/commonUtil';
import type { ClientDeploymentDocument } from 'common/deployment/ClientDeploymentDocument';
import { EditAppContext, useEditApp } from 'client/lib/EditApp';
import { useApi } from 'client/lib/trpc';
import { useAccount, useUser } from 'client/lib/User';
import { type DeploymentClientState, useDeploymentClientState } from 'client/state/DeploymentClientState';
import { CoreLimitDialog } from 'client/pages/deployment/components/CoreLimitDialog';
import { NoCreditsDialog } from 'client/pages/deployment/components/NoCreditsDialog';
import { SettingsGit } from 'client/pages/deployment/components/SettingsGit';
import { SettingsVM } from 'client/pages/deployment/components/SettingsVM';
import { BreadcrumbsItemProps } from 'client/components/Breadcrumbs';
import { Button } from 'client/components/Button';
import { EditAppSaveButton } from 'client/components/EditApp/EditAppSaveButton';
import { HeroAccordion } from 'client/components/Hero/Hero';
import { Settings, Section, Actions, SectionSkeleton } from 'client/components/Layout/Settings';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { Skeleton } from 'client/components/Skeleton';
import { Subheader } from 'client/components/Subheader';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { Deployment } from 'client/illustrations/Deployment';
import { Repository } from 'client/sections/repository/Repository';
import { ComponentsSections } from '../../sections/components/ComponentsSections';
import { SettingsGeneral } from './components/SettingsGeneral';

export const CreateDeploymentPage = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { cloneOfDeploymentID, ...params } = useParams();
  const { user, updateSession } = useUser();
  const account = useAccount();
  const [cloneOfDeploymentName, isCloneFromState, stage] = useDeploymentClientState((state) => [
    state.document?.cloneOfDeploymentName,
    state.document?.cloneOfDeploymentID ? state.document.cloneOfDeploymentID > 0 : false,
    state.document?.stage,
  ]);
  const isQuickLaunch = useDeploymentClientState((state) => state.document?.repositoryUrl == null);

  // FIXME: Limit the amount of running cores for "Free" accounts (for now)
  const [isCoreLimitDialogOpen, setIsCoreLimitDialogOpen] = useState(false);
  const [isNoCreditsDialogOpen, setIsNoCreditsDialogOpen] = useState(false);

  const hasDeployments = user?.projects.some((project) => project.deploymentIDs.length > 0);

  const projectID = parseInt(params.projectID || '0');
  const project = user?.projects.find((project) => project.id === projectID);
  const projectName = project?.name || '';

  const handleSave = useCallback(
    (_: unknown, id: number) => {
      if (!id) return;
      updateSession();
      navigate(`../deployment/${id}`, { state: { createdDeployment: id } });
      window.scrollTo(0, 0);
    },
    [navigate, updateSession]
  );

  const handleIsSavedCheck = useCallback(() => true, []);

  const editAppContext = useEditApp<ClientDeploymentDocument, DeploymentClientState>({
    idKey: 'deploymentID',
    // @ts-expect-error useEditApp does not support tRPC properly
    loadUrl: api.deployment.load,
    // @ts-expect-error useEditApp does not support tRPC properly
    saveUrl: api.deployment.save,
    store: useDeploymentClientState,
    // FIXME: Re-do this!
    // validator: validateDeployment,
    validator: () => new z.ZodError<ClientDeploymentDocument>([]),
    isDocumentSaved: handleIsSavedCheck,
    getLoadParams: () => ({ projectID, cloneOfDeploymentID: parseInt(cloneOfDeploymentID || '0') }),
    getSaveParams: (state) => {
      if (!state.document) return {};

      const document = {
        id: state.document.id,
        projectID: state.project?.id,
        name: state.document.name,
        stage: state.document.stage,
        coreInstanceTypeID: state.document.coreInstanceTypeID,
        cloneOfDeploymentID: state.document.cloneOfDeploymentID,
        allowEnableFromURL: state.document.allowEnableFromURL,
        gitConfigUser: state.document.gitConfigUser,
        componentsWithValues: state.document.componentsWithValues.map((component) => ({
          ...component,
          // Skip empty ENVs
          environmentVariables: component.environmentVariables.filter((env) => env.identifier.length > 0),
        })),
      };

      return { document };
    },
    onSave: handleSave,
    onError: (error) => {
      if (error === 'core limit reached') {
        setIsCoreLimitDialogOpen(true);
        return false;
      }
      if (error === 'no credits') {
        setIsNoCreditsDialogOpen(true);
        return false;
      }
      return true;
    },
  });

  const isClone = (editAppContext.isReady && isCloneFromState) || (cloneOfDeploymentID && cloneOfDeploymentID !== '0');
  const title = isClone ? 'Clone Deployment' : 'Create Deployment';

  const breadcrumbs = useMemo<BreadcrumbsItemProps[]>(
    () => [
      {
        title: 'Projects',
        href: '../../projects',
      },
      {
        title: projectName || '',
        image: { alt: 'project image', src: getPlaceholderUrl('project', projectID), shape: 'hexagon' },
        href: '../',
      },
      {
        title: title,
        disabled: true,
      },
    ],
    [projectName, projectID, title]
  );

  let subtitle: string | JSX.Element = 'Guided setup to create a new deployment';
  if (isClone && !editAppContext.isReady) {
    subtitle = (
      <>
        Cloning from <Skeleton inline width={140} />
      </>
    );
  } else if (isClone) {
    subtitle = (
      <>
        Cloning from{' '}
        <Link to={`/${account?.identifier}/project/${projectID}/deployment/${cloneOfDeploymentID}`} target="_blank" css={cloneTagStyle}>
          <Tag>
            {cloneOfDeploymentName} <ArrowSquareOut weight="bold" />
          </Tag>
        </Link>
      </>
    );
  }

  const isDevelopment = stage === 'development';

  return (
    <EditAppContext.Provider value={editAppContext}>
      <Settings>
        <Subheader
          title={title}
          subtitle={subtitle}
          icon={<ListItemFigure icon={<HardDrives />} ornament={isClone ? <Copy /> : <PlusCircle />} />}
          breadcrumbs={breadcrumbs}
          actions={
            <Button
              icon={<BookOpenText weight="bold" />}
              tooltip="Documentation"
              tooltipSide="left"
              href="https://docs.diploi.com/concepts/deployment"
              external
            />
          }
        />

        {/*!isClone && (
          <HeroAccordion
            id="createDeployment.hero"
            title="What is a deployment?"
            subtitle="And why do I need to set one up?"
            illustration={<Deployment />}
            initialOpen={!hasDeployments}
          >
            <Typography>
              🚀 A deployment in Diploi is like a virtual machine that runs a certain version of your project. This version can then be
              configured, used & developed on without affecting any of the other deployments.
            </Typography>
            <br />
            <Typography>
              💻 Deployments can run multiple services under them, like node processes, databases or even CDNs like WordPress. The way these
              services are run is dictated by the project template & the options it provides.
            </Typography>
            <br />
            <Typography>
              👉 All deployments in a project use the same template, but can have differing options configured in it. State, data & code is
              never shared between deployments, unless the template specifically requests it.
            </Typography>
          </HeroAccordion>
        )*/}

        {!editAppContext.isReady && <SectionSkeleton />}

        {editAppContext.isReady && (
          <>
            <Section title="General" subtitle="Basic information about this deployment.">
              <SettingsGeneral showStage />
            </Section>

            {/* <Section title="Hosts" subtitle="Configure endpoints used to connect to different services running in this deployment.">
              <SettingsHosts />
            </Section> */}

            <Section title="Size" subtitle="Configure the computing resources for this deployment.">
              <SettingsVM />
            </Section>

            {!isQuickLaunch && (
              <Section title="Repository" subtitle="Configure the Git repositories & branches this deployment pulls its code from.">
                <Repository forceBranchPicker />
              </Section>
            )}

            <ComponentsSections />

            {/* <Section title="Environment" subtitle="Edit environment variables.">
              <SettingsEnvironment />
            </Section> */}

            <Actions>
              <EditAppSaveButton icon={<Plus weight="regular" />} primary large>
                {title}
              </EditAppSaveButton>
            </Actions>
          </>
        )}
      </Settings>

      <CoreLimitDialog open={isCoreLimitDialogOpen} onClose={() => setIsCoreLimitDialogOpen(false)} />
      <NoCreditsDialog open={isNoCreditsDialogOpen} onClose={() => setIsNoCreditsDialogOpen(false)} />
    </EditAppContext.Provider>
  );
};

const cloneTagStyle = css`
  text-decoration: none;

  .tag {
    user-select: none;
    cursor: pointer;

    > svg {
      opacity: 0.7;
    }
  }
`;
